import { useSeoMeta, useHead } from '@unhead/react';
import { Link } from 'react-router-dom';
import {Briefcase, Building2, Calculator, FileText, HelpCircle, Home, Info, MapPinned, Phone, PoundSterling, Star, } from 'lucide-react';
import { SiteShell } from '@/components/SiteShell';
import { PageHero } from '@/components/PageHero';
import { normalizeCanonicalUrl } from '@/lib/seo';
import { areaPageConfigs } from '@/lib/areaPages';
import { blogPostConfigs } from '@/lib/blogPosts';
import { longDistanceRemovalsPageConfigs } from '@/lib/longDistanceRemovalsPages';
import { manAndVanPageConfigs } from '@/lib/manAndVanPages';
import { officeRemovalPageConfigs } from '@/lib/officeRemovalPages';
import sitemapBg from '../../images/VIP House Movers Removal Boxes.jpg';
import { packingServicesPageConfigs } from '@/lib/packingServicesPages';
import { sameDayRemovalsPageConfigs } from '@/lib/sameDayRemovalsPages';
import { servicePageConfigs } from '@/lib/servicePages';
import { storageServicesPageConfigs } from '@/lib/storageServicesPages';
import { studentMovesPageConfigs } from '@/lib/studentMovesPages';

type CoreLink = { name: string; path: string; icon: typeof Home; description: string };

const corePages: CoreLink[] = [
  { name: 'Home', path: '/', icon: Home, description: 'Welcome to VIP House Movers' },
  { name: 'Services', path: '/services', icon: Briefcase, description: 'All removal services we provide' },
  { name: 'Get a Quote', path: '/quote', icon: Calculator, description: 'Request a tailored moving quote' },
  { name: 'Areas We Cover', path: '/areas-we-cover', icon: MapPinned, description: 'Locations we serve across the Midlands' },
  { name: 'Pricing', path: '/pricing', icon: PoundSterling, description: 'Transparent pricing and packages' },
  { name: 'Reviews', path: '/reviews', icon: Star, description: 'Customer testimonials and Google-style reviews' },
  { name: 'About Us', path: '/about', icon: Info, description: 'Our story, values, and eco-friendly approach' },
  { name: 'Blog', path: '/blog', icon: FileText, description: 'Moving tips and guides' },
  { name: 'FAQs', path: '/faq', icon: HelpCircle, description: 'Answers to common moving questions' },
  { name: 'Contact', path: '/contact', icon: Phone, description: 'Get in touch with our team' },
];

function SectionHeader({ icon: Icon, title, subtitle, count }: { icon: typeof Home; title: string; subtitle: string; count: number }) {
  return (
    <div className="flex items-start gap-4 mb-6">
      <div className="bg-[#D4AF37] p-3 rounded-xl shadow-md shrink-0">
        <Icon className="w-6 h-6 text-[#111827]" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{title}</h2>
          <span className="bg-[#D4AF37]/15 text-[#8a7118] text-sm font-semibold px-3 py-1 rounded-full">
            {count} {count === 1 ? 'page' : 'pages'}
          </span>
        </div>
        <p className="text-gray-600 mt-1">{subtitle}</p>
      </div>
    </div>
  );
}

function LinkCard({ to, title, subtitle }: { to: string; title: string; subtitle?: string }) {
  return (
    <Link
      to={to}
      className="group flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 bg-white hover:border-[#D4AF37] hover:shadow-md transition-all"
    >
      <div className="min-w-0">
        <h3 className="font-semibold text-gray-900 group-hover:text-[#D4AF37] transition-colors truncate">{title}</h3>
        {subtitle ? <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p> : null}
      </div>
      <span className="text-gray-300 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all font-bold">
        &rarr;
      </span>
    </Link>
  );
}

export default function Sitemap() {
  const totalPages =
    corePages.length +
    servicePageConfigs.length +
    officeRemovalPageConfigs.length +
    manAndVanPageConfigs.length +
    packingServicesPageConfigs.length +
    storageServicesPageConfigs.length +
    sameDayRemovalsPageConfigs.length +
    studentMovesPageConfigs.length +
    longDistanceRemovalsPageConfigs.length +
    blogPostConfigs.length +
    areaPageConfigs.length;

  useSeoMeta({
    title: 'Sitemap | VIP House Movers Wolverhampton | Removals Service Pages',
    description: 'Full sitemap for VIP House Movers Wolverhampton. Browse all removals service pages, area pages, blog articles, quotes, pricing and contact pages.',
    ogTitle: 'Sitemap | VIP House Movers Wolverhampton',
    ogDescription: 'Browse all pages on the VIP House Movers website including services, areas, blog articles and more.',
    ogImage: 'https://www.viphousemovers.com/og-home.jpg',
    ogUrl: 'https://www.viphousemovers.com/sitemap',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Sitemap | VIP House Movers Wolverhampton',
    twitterDescription: 'Full sitemap for VIP House Movers Wolverhampton website.',
    twitterImage: 'https://www.viphousemovers.com/og-home.jpg',
  });
  const canonicalHref = normalizeCanonicalUrl('https://www.viphousemovers.com/sitemap');

  useHead({
    link: [{ rel: 'canonical', href: canonicalHref }],
    meta: [
      {
        name: 'keywords',
        content: 'VIP House Movers sitemap, removals website sitemap, area page index, service page directory, local removal pages, Wolverhampton removals site map',
      },
    ],
  });

  return (
    <SiteShell activePath="/sitemap" fixedNav>
      <PageHero
        eyebrow="Site Map"
        title="Every Page on VIP House Movers"
        description={`Browse all ${totalPages} pages of our website. This sitemap updates automatically as new services, locations, and areas are added.`}
        align="center"
        compact
        backgroundImage={sitemapBg}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        {/* Core Pages */}
        <section>
          <SectionHeader
            icon={Home}
            title="Core Pages"
            subtitle="Primary navigation and key site destinations"
            count={corePages.length}
          />
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {corePages.map((page) => (
                <li key={page.path}>
                  <Link
                    to={page.path}
                    className="flex items-center p-5 hover:bg-gray-50 transition-colors group"
                  >
                    <div className="bg-[#D4AF37]/10 p-3 rounded-lg mr-4 group-hover:bg-[#D4AF37] transition-colors duration-300">
                      <page.icon className="w-5 h-5 text-[#D4AF37] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[#D4AF37] transition-colors">
                        {page.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5 truncate">{page.description}</p>
                    </div>
                    <code className="hidden sm:block text-xs text-gray-400 mr-3 font-mono">{page.path}</code>
                    <span className="text-gray-300 group-hover:text-[#D4AF37] transition-colors font-bold text-xl">
                      &rarr;
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Services */}
        <section>
          <SectionHeader
            icon={Briefcase}
            title="Services"
            subtitle="Dedicated pages for every removal service we offer"
            count={servicePageConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {servicePageConfigs.map((service) => (
              <LinkCard
                key={service.path}
                to={service.path}
                title={service.navLabel}
                subtitle={service.path}
              />
            ))}
          </div>
        </section>

        {/* Office Removals by Location */}
        <section>
          <SectionHeader
            icon={Building2}
            title="Office Removals by Location"
            subtitle="Location-specific office removal pages"
            count={officeRemovalPageConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {officeRemovalPageConfigs.map((office) => (
              <LinkCard
                key={office.path}
                to={office.path}
                title={`Office Removals \u2013 ${office.navLabel}`}
                subtitle={office.path}
              />
            ))}
          </div>
        </section>

        {/* Man and Van by Location */}
        <section>
          <SectionHeader
            icon={Briefcase}
            title="Man and Van by Location"
            subtitle="Location-specific Man and Van service pages"
            count={manAndVanPageConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {manAndVanPageConfigs.map((mav) => (
              <LinkCard
                key={mav.path}
                to={mav.path}
                title={`Man and Van \u2013 ${mav.navLabel}`}
                subtitle={mav.path}
              />
            ))}
          </div>
        </section>

        {/* Packing Services by Location */}
        <section>
          <SectionHeader
            icon={Briefcase}
            title="Packing Services by Location"
            subtitle="Location-specific packing service pages"
            count={packingServicesPageConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {packingServicesPageConfigs.map((pack) => (
              <LinkCard
                key={pack.path}
                to={pack.path}
                title={`Packing Services \u2013 ${pack.navLabel}`}
                subtitle={pack.path}
              />
            ))}
          </div>
        </section>

        {/* Storage Services by Location */}
        <section>
          <SectionHeader
            icon={Briefcase}
            title="Storage Services by Location"
            subtitle="Location-specific storage service pages"
            count={storageServicesPageConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {storageServicesPageConfigs.map((storage) => (
              <LinkCard
                key={storage.path}
                to={storage.path}
                title={`Storage Services \u2013 ${storage.navLabel}`}
                subtitle={storage.path}
              />
            ))}
          </div>
        </section>

        {/* Same Day Removals by Location */}
        <section>
          <SectionHeader
            icon={Briefcase}
            title="Same Day Removals by Location"
            subtitle="Location-specific same day removal pages"
            count={sameDayRemovalsPageConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {sameDayRemovalsPageConfigs.map((sameDay) => (
              <LinkCard
                key={sameDay.path}
                to={sameDay.path}
                title={`Same Day Removals \u2013 ${sameDay.navLabel}`}
                subtitle={sameDay.path}
              />
            ))}
          </div>
        </section>

        {/* Student Moves by Location */}
        <section>
          <SectionHeader
            icon={Briefcase}
            title="Student Moves by Location"
            subtitle="Location-specific student moves pages"
            count={studentMovesPageConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {studentMovesPageConfigs.map((student) => (
              <LinkCard
                key={student.path}
                to={student.path}
                title={`Student Moves \u2013 ${student.navLabel}`}
                subtitle={student.path}
              />
            ))}
          </div>
        </section>

        {/* Long Distance Removals by Location */}
        <section>
          <SectionHeader
            icon={Briefcase}
            title="Long Distance Removals by Location"
            subtitle="Location-specific long distance removal pages"
            count={longDistanceRemovalsPageConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {longDistanceRemovalsPageConfigs.map((ld) => (
              <LinkCard
                key={ld.path}
                to={ld.path}
                title={`Long Distance Removals \u2013 ${ld.navLabel}`}
                subtitle={ld.path}
              />
            ))}
          </div>
        </section>

        {/* Blog / SEO Articles */}
        <section>
          <SectionHeader
            icon={FileText}
            title="Blog & SEO Articles"
            subtitle="In-depth moving guides, checklists and expert tips"
            count={blogPostConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {blogPostConfigs.map((post) => (
              <LinkCard
                key={post.path}
                to={post.path}
                title={post.title}
                subtitle={post.path}
              />
            ))}
          </div>
        </section>

        {/* Areas We Cover */}
        <section>
          <SectionHeader
            icon={MapPinned}
            title="Areas We Cover"
            subtitle="Dedicated landing pages for every location we serve"
            count={areaPageConfigs.length}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {areaPageConfigs.map((area) => (
              <LinkCard
                key={area.path}
                to={area.path}
                title={area.navLabel}
                subtitle={area.path}
              />
            ))}
          </div>
        </section>

        {/* Summary */}
        <section className="bg-[#111827] text-white rounded-2xl p-8 md:p-10 text-center">
          <p className="text-[#D4AF37] font-semibold uppercase tracking-wider text-sm mb-2">Sitemap Summary</p>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {totalPages} pages, always up to date
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto mb-6">
            This sitemap is generated automatically from the VIP House Movers route configuration, so any new
            service, office location, or area page is added here the moment it goes live.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/quote"
              className="bg-[#D4AF37] text-[#111827] font-semibold px-6 py-3 rounded-lg hover:bg-[#c9a332] transition-colors"
            >
              Get a Free Quote
            </Link>
            <Link
              to="/contact"
              className="border border-white/30 text-white font-semibold px-6 py-3 rounded-lg hover:bg-white/10 transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </section>
      </div>
    </SiteShell>
  );
}

